// AI ジョブ管理 — 全種類のバックグラウンドAIタスクを一元管理する
//   • 週間ダイジェスト生成 (digest)
//   • ダイジェスト英訳     (translate)
//   • RAG索引/NASクローラー (crawler)
// 各ジョブは `cancel_url` を持ち、フロントは種別ごとに分岐せずそのままPOSTする。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, RefreshCcw, Activity, FileText, Languages, Database } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/ai/console`;

const KIND_META = {
  digest: { Icon: FileText, tone: 'violet' },
  translate: { Icon: Languages, tone: 'indigo' },
  crawler: { Icon: Database, tone: 'teal' },
};
const TONE_CLASS = {
  violet: 'bg-violet-50 border-violet-200 text-violet-700',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  teal: 'bg-teal-50 border-teal-200 text-teal-700',
  zinc: 'bg-zinc-50 border-zinc-200 text-zinc-700',
};

const fmtElapsed = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function AiJobsSection() {
  const [jobs, setJobs] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState({});

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/jobs`, { withCredentials: true });
      setJobs(r.data.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ジョブ一覧の取得に失敗しました');
      setJobs([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const cancel = async (job) => {
    if (!window.confirm(`「${job.kind_label_ja}: ${job.title}」を中止しますか?`)) return;
    setCancelling((s) => ({ ...s, [job.id]: true }));
    try {
      // cancel_url is server-issued; pattern is /api/... — prepend backend host.
      await axios.post(`${BACKEND}${job.cancel_url}`, {}, { withCredentials: true });
      toast.success('中止リクエストを送信しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '中止に失敗しました');
    } finally {
      setCancelling((s) => ({ ...s, [job.id]: false }));
    }
  };

  if (jobs === null) return <p className="text-sm text-zinc-400">読み込み中…</p>;

  return (
    <div className="space-y-4" data-testid="ai-jobs-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <Activity size={14} className="text-indigo-500" /> 実行中のAIジョブ
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            週間ダイジェスト・英訳・RAG索引/NASクローラーを含む、全てのバックグラウンドAI処理をここから中止できます。
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="ai-jobs-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-50 disabled:opacity-50">
          <RefreshCcw size={12} className={refreshing ? 'animate-spin' : ''} /> 更新
        </button>
      </div>

      {jobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-white px-5 py-8 text-center" data-testid="ai-jobs-empty">
          <p className="text-sm text-zinc-500">実行中のAIジョブはありません</p>
          <p className="mt-1 text-[11px] text-zinc-400">
            ダイジェスト生成 / 英訳 / RAG索引を開始すると、ここに進行状況が表示されます。
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full min-w-[860px] text-[12px]" data-testid="ai-jobs-table">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">種類</th>
                <th className="text-left px-4 py-2 font-semibold">対象</th>
                <th className="text-left px-4 py-2 font-semibold">進行</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">経過</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">起動者</th>
                <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const meta = KIND_META[j.kind] || { Icon: Activity, tone: 'zinc' };
                const { Icon } = meta;
                const step = j.progress?.step ?? 0;
                const total = j.progress?.total || 1;
                const pct = Math.min(100, Math.round((step / total) * 100));
                return (
                  <tr key={j.id} className="border-t border-zinc-200" data-testid={`ai-job-row-${j.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold whitespace-nowrap ${TONE_CLASS[meta.tone] || TONE_CLASS.zinc}`}>
                        <Icon size={10} /> {j.kind_label_ja}
                      </span>
                      {!j.live && (
                        <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap" title="バックエンドの再起動により制御タスクは失われました。中止するとDBステータスのみ更新されます。">
                          孤立
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-zinc-700 truncate max-w-[220px]" title={j.title}>{j.title}</td>
                    <td className="px-4 py-3 min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] tabular-nums text-zinc-500 shrink-0">{step}/{total}</span>
                      </div>
                      <p className="mt-1 text-[10.5px] text-zinc-500 truncate max-w-[280px]" title={j.progress?.label}>
                        {j.progress?.label || '起動中…'}
                      </p>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-zinc-600 whitespace-nowrap">{fmtElapsed(j.started_at)}</td>
                    <td className="px-4 py-3 text-zinc-600 truncate max-w-[140px]" title={j.actor}>{j.actor || '—'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => cancel(j)}
                        disabled={!!cancelling[j.id]}
                        data-testid={`ai-job-cancel-${j.id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50 whitespace-nowrap">
                        <Trash2 size={11} /> {cancelling[j.id] ? '中止中…' : '中止'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
